import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOnboarding } from './ImportJobContext';
import { IMPORT_ACCEPT } from './flags';
import { sourceTag } from './onboarding.service';
import { IconTick, IconPlus, IconDoc, IconPhoto, IconShield, IconClose, IconWarn } from './icons';

/**
 * Step 1: the staging area (plan §4.2).
 * Left (desktop) / bottom (mobile): dropzone, one row per file, counters,
 * trace lines, Continue setup. Right / top: the charcoal story panel.
 * No auto-advance. Dropping more stays possible throughout.
 */

const fmt = (n) => (n || 0).toLocaleString('en-GB');

/* Counts up from the previously shown value to `to` over 1.6s ease-out. */
function useCountUp(to) {
  const [shown, setShown] = useState(to || 0);
  const fromRef = useRef(to || 0);
  useEffect(() => {
    const from = fromRef.current;
    const target = to || 0;
    if (from === target) return undefined;
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { fromRef.current = target; setShown(target); return undefined; }
    // Interval, not rAF: rAF pauses in background tabs and would leave the
    // counters frozen mid-count if the owner switches away while reading.
    const t0 = Date.now();
    const int = setInterval(() => {
      const p = Math.min((Date.now() - t0) / 1600, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(from + (target - from) * e));
      if (p >= 1) { fromRef.current = target; clearInterval(int); }
    }, 45);
    return () => { clearInterval(int); fromRef.current = target; setShown(target); };
  }, [to]);
  return shown;
}

function Counter({ n, label, compact }) {
  const shown = useCountUp(n);
  if (compact) {
    return (
      <div className={`ob-counter ${!n ? 'ob-counter--zero' : ''}`}>
        <div className="ob-counter-n">{fmt(shown)}</div>
        <div className="ob-counter-l">{label}</div>
      </div>
    );
  }
  if (!n) return null;
  return <div className="ob-story-counter"><b>{fmt(shown)}</b><span>{label.toLowerCase()}</span></div>;
}

function rowTag(row) {
  if (row.status === 'failed') return { cls: 'ob-row-tag--fail', text: row.failReason || 'Could not upload', icon: <IconWarn size={12} /> };
  if (row.status === 'needs_look') return { cls: 'ob-row-tag--warn', text: row.analysis?.tag || 'We will ask you about this', icon: <IconWarn size={12} /> };
  if (row.status === 'recognised' && row.analysis) {
    const a = row.analysis;
    if (a.tag) return { cls: '', text: a.tag, icon: <IconTick size={12} /> };
    const primary = ['clients', 'services', 'products', 'staff'].find((k) => a.counts?.[k]);
    const n = primary ? a.counts[primary] : null;
    const text = a.source === 'photo' || a.source === 'pdf'
      ? `${n ? fmt(n) + ' ' + primary : 'Read'}`
      : `${sourceTag(a.source)} · ${n ? fmt(n) : ''}`.replace(/ · $/, '');
    return { cls: '', text, icon: <IconTick size={12} /> };
  }
  // uploading / reading: show the local pre-read as a muted hint if we have one
  if (row.preread?.kinds?.includes('bookings')) return { cls: 'ob-row-tag--muted', text: "Appointment exports aren't imported", icon: <span className="ob-spinner" /> };
  if (row.preread?.tag && row.preread.rows) return { cls: 'ob-row-tag--muted', text: row.preread.tag, icon: <span className="ob-spinner" /> };
  return { cls: 'ob-row-tag--muted', text: row.status === 'uploading' ? 'Uploading' : 'Reading', icon: <span className="ob-spinner" /> };
}

export function Dropzone({ onFiles, title, sub, compact, busy }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const pick = () => inputRef.current && inputRef.current.click();
  const onChange = (e) => { onFiles(e.target.files); e.target.value = ''; };
  const onDrop = (e) => { e.preventDefault(); setOver(false); if (e.dataTransfer?.files?.length) onFiles(e.dataTransfer.files); };
  return (
    <div
      className={`ob-drop ${busy ? 'ob-drop--busy' : ''} ${over ? 'ob-drop--over' : ''}`}
      role="button"
      tabIndex={0}
      onClick={pick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      aria-label="Add files"
    >
      <span className="ob-drop-plus"><IconPlus /></span>
      <div className="ob-drop-title">{title}</div>
      {sub && <div className="ob-drop-sub">{sub}</div>}
      <input ref={inputRef} type="file" multiple accept={IMPORT_ACCEPT} onChange={onChange} />
    </div>
  );
}

export function StagedRows({ rows, onRemove }) {
  if (!rows.length) return null;
  return (
    <div className="ob-rows" role="list" aria-label="Files added">
      {rows.map((row) => {
        const tag = rowTag(row);
        return (
          <div className="ob-row" role="listitem" key={row.localId}>
            <span className="ob-row-icon">{row.isDoc ? <IconDoc /> : <IconPhoto />}</span>
            <span className="ob-row-name" title={row.name}>{row.name}</span>
            <span className={`ob-row-tag ${tag.cls}`}>{tag.icon}<span>{tag.text}</span></span>
            {onRemove && row.status === 'failed' && (
              <button type="button" className="ob-row-remove" onClick={() => onRemove(row.localId)} aria-label={`Remove ${row.name}`}><IconClose size={12} /></button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function TraceList({ items, limit }) {
  const list = limit ? items.slice(-limit) : items;
  if (!list.length) return null;
  return (
    <div className="ob-trace">
      {list.map((t, i) => <div className="ob-trace-item" key={`${t}-${i}`}><IconTick size={12} />{t}</div>)}
    </div>
  );
}

export const SafeLine = () => (
  <div className="ob-safe"><IconShield /><span>Nothing has been sent to your clients</span></div>
);

export function jobTotals(job) {
  const s = job?.summary || {};
  // No bookings: import brings in clients, services and products only (docs §6a).
  return { clients: s.clients || 0, services: s.services || 0, products: s.products || 0, staff: s.staff || 0, questions: s.questions || 0 };
}

export function jobTrace(job) {
  const out = [];
  (job?.files || []).forEach((f) => { if (f.status === 'recognised' && f.analysis?.trace) out.push(...f.analysis.trace); });
  return out;
}

/**
 * variant="setup" (default): step 1 of onboarding, "Continue setup".
 * variant="more": the same screen after setup ("Bring more in"), where the
 * primary action adds straight to the salon and closes.
 */
export default function ImportStaging({ salonName, onContinue, onSkip, isMobile, variant = 'setup', committing = false }) {
  const more = variant === 'more';
  const ob = useOnboarding();
  const { stagedRows, job, error, addFiles, removeLocalFile } = ob;
  const totals = jobTotals(job);
  const trace = jobTrace(job);
  const hasFiles = stagedRows.length > 0;
  const hasDone = stagedRows.some((r) => r.status === 'recognised' || r.status === 'needs_look');
  const busy = stagedRows.some((r) => r.status === 'uploading' || r.status === 'reading');
  const anyRecognised = totals.clients + totals.services + totals.products > 0;

  const onFiles = useCallback((files) => addFiles(files), [addFiles]);

  const dropTitle = isMobile ? 'Add files or take a photo' : 'Drag and drop anything';
  const dropSub = !hasFiles
    ? (isMobile ? 'CSV, Excel, PDF or a photo. We will work out what each one is.' : 'or click to browse. CSV, Excel, PDF or a photo.')
    : 'Keep going. Add another whenever you like.';

  const imported = totals.clients || totals.services || totals.products;

  // Who the dedupe matched to clients the salon already has, and on what. A
  // bare count read as lost rows: "7 clients" from a 9-row file looked like two
  // people had vanished; they were already here, matched by phone. Older jobs
  // carry only the count, so fall back to that.
  const alreadyInRows = Array.isArray(job?.alreadyInRows) ? job.alreadyInRows : [];
  const alreadyInCount = Number(job?.alreadyIn) || alreadyInRows.length;
  const alreadyInText = !alreadyInCount ? '' : alreadyInRows.length
    ? `Already in your salon, so not added again: ${alreadyInRows.slice(0, 3).map((r) => `${r.name}${r.matchedOn ? ` (same ${r.matchedOn})` : ''}`).join(', ')}${alreadyInRows.length > 3 ? ` and ${alreadyInRows.length - 3} more` : ''}.`
    : `${alreadyInCount} already in your salon, so not added again.`;
  const ctaSub = more
    ? `${fmt(imported)} ${totals.clients ? 'clients' : totals.services ? 'services' : 'products'} read · merged with what you already have`
    : `${fmt(imported)} ${totals.clients ? 'clients' : totals.services ? 'services' : 'products'} read · ${totals.questions ? `${totals.questions} quick question${totals.questions === 1 ? '' : 's'} to finish` : 'nothing else to ask'}`;

  return (
    <>
      <div className="ob-story" aria-hidden={isMobile ? undefined : 'false'}>
        <div className="ob-story-eyebrow">{more ? 'Bring more in' : `Setting up ${salonName} · Step 1 of 4`}</div>
        <div className="ob-float ob-float--chip ob-float--csv">.csv</div>
        <div className="ob-float ob-float--chip ob-float--xlsx">.xlsx</div>
        {!hasFiles && (
          <div className="ob-float ob-float--diary" aria-hidden="true">
            <div><b>Sarah Jenkins, Cut &amp; Finish</b>09:00-10:00 · Nadia</div>
            <div><b>Ruth Kelly, Balayage</b>13:00-15:00 · Jess</div>
          </div>
        )}
        {!hasFiles && (
          <div className="ob-float ob-float--card" aria-hidden="true">
            <div className="ob-av">SJ</div>
            <div><b>Sarah Jenkins</b><span>14 visits · since 2021</span></div>
          </div>
        )}
        <div className="ob-story-head">
          <h1 className="ob-story-h1">{more ? 'Bring in more.' : "Bring everything you've got."}</h1>
          <p className="ob-story-h2">{more ? <>Anything you've got, we'll <em>sort</em> it.</> : <>This could be the start of something <em>beautiful</em>.</>}</p>
          <p className="ob-story-p">{more ? <>Client lists, price lists, product sheets: a spreadsheet, a PDF or a photo.<br />We merge it with what you already have.</> : <>Client lists, price lists, product sheets: a spreadsheet, a PDF or a photo.<br />Drop it anywhere on this window.</>}</p>
          {anyRecognised && (
            <>
              <div className="ob-story-counters">
                <Counter n={totals.clients} label="Clients" />
                <Counter n={totals.services} label="Services" />
                <Counter n={totals.products} label="Products" />
              </div>
              <div className="ob-story-trace"><TraceList items={trace} limit={2} /></div>
            </>
          )}
        </div>
      </div>

      <div className="ob-files">
        <h2 className="ob-files-title">Add your files</h2>
        <Dropzone onFiles={onFiles} title={dropTitle} sub={dropSub} busy={busy} />
        {error && <div className="ob-error" role="alert">{error}</div>}
        <StagedRows rows={stagedRows} onRemove={removeLocalFile} />
        {hasFiles && (
          <>
            <div className="ob-counters" aria-live="polite">
              <Counter compact n={totals.clients} label="Clients" />
              <Counter compact n={totals.services} label="Services" />
              <Counter compact n={totals.products} label="Products" />
            </div>
            {alreadyInText && <p className="ob-already-in">{alreadyInText}</p>}
            {isMobile && <TraceList items={trace} limit={3} />}
          </>
        )}
        <div className="ob-files-foot">
          {hasDone && (
            <div>
              <button type="button" className="ob-btn ob-btn--primary" onClick={onContinue} disabled={committing || (busy && !anyRecognised)}>{more ? (committing ? 'Adding…' : 'Add to my salon') : 'Continue setup'}</button>
              <div className="ob-cta-sub">{ctaSub}</div>
            </div>
          )}
          {!hasFiles && !more && (
            <button type="button" className="ob-link-btn" onClick={onSkip}>I'll set things up myself</button>
          )}
          <SafeLine />
        </div>
      </div>
    </>
  );
}
